import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, getRoles } from '../services/api';
import { Plus, Edit2, Trash2, User, Check, X, Eye, EyeOff, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';

export default function Users() {
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '', password: '', full_name: '', email: '',
        role_id: '', is_admin: false, pin: '',
        hourly_rate: '', overtime_rate: '', monthly_hours: '',
        work_start_time: '', work_end_time: '',
    });

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [employeesRes, rolesRes] = await Promise.all([getEmployees(), getRoles()]);
            setEmployees(employeesRes.data);
            setRoles(rolesRes.data);
        } catch { toast.error('Kullanicilar yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handleOpenModal = (employee = null) => {
        setShowPassword(false);
        if (employee) {
            setEditingEmployee(employee);
            setFormData({
                username: employee.username, password: employee.password_plain || '',
                full_name: employee.full_name, email: employee.email || '',
                role_id: employee.role_id || '', is_admin: employee.is_admin,
                pin: employee.pin || '', hourly_rate: employee.hourly_rate || '',
                overtime_rate: employee.overtime_rate || '', monthly_hours: employee.monthly_hours || '',
                work_start_time: employee.work_start_time || '', work_end_time: employee.work_end_time || '',
            });
        } else {
            setEditingEmployee(null);
            setFormData({
                username: '', password: '', full_name: '', email: '',
                role_id: '', is_admin: false, pin: '',
                hourly_rate: '', overtime_rate: '', monthly_hours: '',
                work_start_time: '', work_end_time: '',
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = { ...formData };
            if (data.hourly_rate !== '' && data.hourly_rate !== null) data.hourly_rate = parseFloat(data.hourly_rate) || null; else data.hourly_rate = null;
            if (data.overtime_rate !== '' && data.overtime_rate !== null) data.overtime_rate = parseFloat(data.overtime_rate) || null; else data.overtime_rate = null;
            if (data.monthly_hours !== '' && data.monthly_hours !== null) data.monthly_hours = parseFloat(data.monthly_hours) || null; else data.monthly_hours = null;
            if (!data.pin) data.pin = null;
            if (!data.work_start_time) data.work_start_time = null;
            if (!data.work_end_time) data.work_end_time = null;
            if (editingEmployee) {
                if (!data.password) delete data.password;
                await updateEmployee(editingEmployee.id, data);
                toast.success('Kullanici guncellendi');
            } else {
                await createEmployee(data);
                toast.success('Kullanici olusturuldu');
            }
            setShowModal(false);
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Kaydetme hatasi'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu kullaniciyi silmek istediginizden emin misiniz?')) return;
        try { await deleteEmployee(id); toast.success('Kullanici silindi'); fetchData(); }
        catch { toast.error('Kullanici silinemedi'); }
    };

    const handleToggleActive = async (employee) => {
        try {
            await updateEmployee(employee.id, { is_active: !employee.is_active });
            toast.success(employee.is_active ? 'Kullanici pasif yapildi' : 'Kullanici aktif yapildi');
            fetchData();
        } catch { toast.error('Durum guncellenemedi'); }
    };

    const filtered = employees.filter(emp =>
        emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="users-page">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="heading-2 mb-1">Kullanicilar</h1>
                        <p className="text-body">{employees.length} kullanici kayitli</p>
                    </div>
                    <button onClick={() => handleOpenModal()} className="btn btn-primary" data-testid="add-user-btn">
                        <Plus size={16} /> Yeni Kullanici
                    </button>
                </div>

                {employees.length > 0 && (
                    <div className="relative max-w-sm">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Kullanici ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input-apple pl-9 text-sm" data-testid="user-search-input" />
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
                ) : filtered.length > 0 ? (
                    <div className="data-card">
                        <div className="table-container">
                            <table className="table mobile-cards" data-testid="users-table">
                                <colgroup>
                                    <col />
                                    <col style={{ width: '140px' }} />
                                    <col style={{ width: '180px' }} />
                                    <col style={{ width: '100px' }} />
                                    <col style={{ width: '80px' }} />
                                    <col style={{ width: '110px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Ad Soyad</th>
                                        <th>Kullanici Adi</th>
                                        <th className="mobile-hide">Email</th>
                                        <th className="mobile-hide">Rol</th>
                                        <th className="text-center">Durum</th>
                                        <th className="w-28 text-right">Islem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(emp => (
                                        <tr key={emp.id} data-testid={`user-row-${emp.id}`}>
                                            <td>
                                                <div className="flex items-center gap-2.5">
                                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-semibold ${emp.is_active ? 'bg-[#1D1D1F]' : 'bg-[#C7C7CC]'}`}>
                                                        {emp.full_name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-[#1D1D1F] text-sm">{emp.full_name}</span>
                                                        {emp.is_admin && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#5856D6]/10 text-[#5856D6] text-[10px] font-semibold">Admin</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-[#86868B] text-sm">@{emp.username}</td>
                                            <td className="mobile-hide text-[#86868B] text-sm">{emp.email || '—'}</td>
                                            <td className="mobile-hide">
                                                {emp.role_name ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-[#F5F5F7] text-[#1D1D1F] text-xs font-medium">{emp.role_name}</span> : <span className="text-[#86868B] text-sm">—</span>}
                                            </td>
                                            <td className="text-center">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${emp.is_active ? 'bg-[#34C759]/10 text-[#34C759]' : 'bg-[#F5F5F7] text-[#86868B]'}`}>
                                                    {emp.is_active ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <div className="flex items-center justify-end gap-0.5">
                                                    <button onClick={() => handleToggleActive(emp)} className="p-1.5 rounded-lg hover:bg-[#F5F5F7] transition-colors" title={emp.is_active ? 'Pasif Yap' : 'Aktif Yap'} data-testid={`toggle-user-${emp.id}`}>
                                                        {emp.is_active ? <X size={15} className="text-[#FF9500]" /> : <Check size={15} className="text-[#34C759]" />}
                                                    </button>
                                                    <button onClick={() => handleOpenModal(emp)} className="p-1.5 rounded-lg hover:bg-[#F5F5F7] transition-colors" title="Duzenle" data-testid={`edit-user-${emp.id}`}>
                                                        <Edit2 size={15} className="text-[#007AFF]" />
                                                    </button>
                                                    <button onClick={() => handleDelete(emp.id)} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 transition-colors" title="Sil" data-testid={`delete-user-${emp.id}`}>
                                                        <Trash2 size={15} className="text-[#FF3B30]" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : employees.length > 0 ? (
                    <div className="card-apple text-center py-12">
                        <Search className="w-12 h-12 text-[#C7C7CC] mx-auto mb-3" />
                        <p className="text-[#86868B]">Aramanizla eslesen kullanici bulunamadi</p>
                    </div>
                ) : (
                    <div className="card-apple text-center py-16">
                        <User className="w-16 h-16 text-[#C7C7CC] mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">Henuz kullanici yok</h3>
                        <p className="text-[#86868B] mb-6">Ekibinizi yonetmek icin kullanici ekleyin</p>
                        <button onClick={() => handleOpenModal()} className="btn btn-primary">
                            <Plus size={16} /> Yeni Kullanici
                        </button>
                    </div>
                )}

                <Dialog open={showModal} onOpenChange={setShowModal}>
                    <DialogContent className="sm:max-w-md" aria-label="Kullanici Formu">
                        <DialogHeader>
                            <DialogTitle data-testid="user-modal-title">{editingEmployee ? 'Kullaniciyi Duzenle' : 'Yeni Kullanici Ekle'}</DialogTitle>
                            <DialogDescription>{editingEmployee ? 'Kullanici bilgilerini guncelleyin.' : 'Yeni bir kullanici olusturun.'}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="user-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Ad Soyad *</label>
                                    <input type="text" value={formData.full_name} onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))} placeholder="Ahmet Yilmaz" className="input-apple" required data-testid="user-fullname-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Kullanici Adi *</label>
                                    <input type="text" value={formData.username} onChange={e => setFormData(p => ({ ...p, username: e.target.value }))} placeholder="ahmetyilmaz" className="input-apple" required data-testid="user-username-input" />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Sifre {editingEmployee ? '' : '*'}</label>
                                <div className="relative">
                                    <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} placeholder={editingEmployee ? 'Degistirmek icin yeni sifre girin' : 'Sifre'} className="input-apple pr-10" required={!editingEmployee} data-testid="user-password-input" />
                                    <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F] transition-colors" data-testid="toggle-password-visibility">
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                                {editingEmployee && formData.password && <p className="text-[10px] text-[#86868B] mt-1">Mevcut sifre gosteriliyor. Degistirmek icin yeni sifre yazin.</p>}
                            </div>

                            <div>
                                <label className="text-xs font-medium text-[#86868B] block mb-1.5">Email</label>
                                <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="ahmet@firma.com" className="input-apple" data-testid="user-email-input" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">Rol</label>
                                    <select value={formData.role_id} onChange={e => setFormData(p => ({ ...p, role_id: e.target.value }))} className="input-apple" data-testid="user-role-select">
                                        <option value="">Rol secin</option>
                                        {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#86868B] block mb-1.5">PIN</label>
                                    <input type="text" value={formData.pin} onChange={e => setFormData(p => ({ ...p, pin: e.target.value }))} placeholder="1234" className="input-apple" maxLength={6} data-testid="user-pin-input" />
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F7]">
                                <input type="checkbox" id="is_admin" checked={formData.is_admin} onChange={e => setFormData(p => ({ ...p, is_admin: e.target.checked }))} className="w-4 h-4 rounded border-[#D2D2D7] text-[#007AFF] focus:ring-[#007AFF]" data-testid="user-admin-checkbox" />
                                <label htmlFor="is_admin" className="text-sm text-[#1D1D1F] font-medium cursor-pointer">Admin yetkisi ver</label>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary flex-1" data-testid="cancel-user-btn">Iptal</button>
                                <button type="submit" className="btn btn-primary flex-1" data-testid="save-user-btn">{editingEmployee ? 'Guncelle' : 'Olustur'}</button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
